import { ProspectingEvent } from "./types.ts";
import CGT from "../../utils/CGT.ts";

function parseEventLocation(locationString: string) {
  const coords = locationString
    .substring(locationString.indexOf("("))
    .replace(/( at )|\(|\)|,/g, " ")
    .split(" ")
    .filter((x) => x)
    .map(Number);

  return {
    galaxy: {
      x: coords[0],
      y: coords[1],
    },
    system: {
      x: coords[2],
      y: coords[3],
    },
  };
}

function parseProspectingResults(sentences: string[]) {
  const firstTrimmed = (sentences[1] ?? "").trim();

  if (firstTrimmed.startsWith("No traces were found")) {
    return { trace: -1 };
  }

  if (firstTrimmed.startsWith("You found")) {
    const foundMatch = firstTrimmed.match(/You found\s+([\d,]+)\s+units?\s+of\s+(.+)/i);
    if (!foundMatch) {
      return { trace: 0 };
    }

    const size = Number(foundMatch[1].replace(/,/g, ""));
    const type = foundMatch[2].trim().toLowerCase();

    if (sentences.length > 2) {
      const trace = sentences[2].startsWith("No nearby trace") ? -1 : Number((sentences[2] ?? "").replace(/[^0-9]/g, ""));
      return { trace: Number.isFinite(trace) ? trace : -1, deposit: { size, type } };
    }

    return { trace: 0, deposit: { size, type } };
  }

  if (firstTrimmed.startsWith("No deposit was found")) {
    const trace = Number((sentences[2] ?? "").replace(/[^0-9]/g, ""));
    return { trace: Number.isFinite(trace) ? trace : 0 };
  }

  if (firstTrimmed.startsWith("A very strong reading")) {
    return { trace: 0, deposit: null };
  }

  return null;
}

function parseEvent(date: number, detailsLine: string): ProspectingEvent | null {
  if (!detailsLine.startsWith("You have completed prospecting within the Asteroid Field")) {
    return null;
  }

  const sentences = detailsLine.split(/\.\s/);
  const eventLocation = parseEventLocation(sentences[0]);
  const prospectingResults = parseProspectingResults(sentences);

  if (!prospectingResults) {
    return null;
  }

  return {
    date,
    ...eventLocation,
    ...prospectingResults,
  } as ProspectingEvent;
}

const nonEmptyOrNotNumber = (line: string) => line && Number.isNaN(Number(line));

export const parseProspectingLog = (logString: string): ProspectingEvent[] => {
  const relevantLines = logString.split('\n')
    .filter(nonEmptyOrNotNumber);
  const events: ProspectingEvent[] = [];
  relevantLines.forEach((line, idx) => {
    if (CGT.isCGTString(line)) {
      if (idx + 1 < relevantLines.length) {
        const date = CGT.parseCGT(line);
        const event = parseEvent(date.toSeconds(), relevantLines[idx + 1]);
        if (event) {
          events.push(event);
        }
      }
    }
  });
  return events;
}
